import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { withTheme } from '@mui/styles';
import { Helmet } from 'react-helmet';
import styled from '@emotion/styled';

import {
  StyledContentContainer,
  CommonLayout,
  StyledButton,
  StyledLink,
  StyledText,
  StyledTitle,
} from '@selfserve/react-ui';
import {
  ApplicationFeatures,
  buttonIds,
  IIndexSignature, PageType, trackPageLoadEvent,
  useSVGAnimation
} from '@selfserve/react-utils';
import { storageKeys, useScreenSizes } from '@selfserve/react-hooks';

import { useGetLoginUrl } from '../hooks/use-get-login-url';

import { useUseCaseRequest, useUseCaseSelect } from '../data';

import ConfirmedAnimationJson from '../assets/confirmed.json';
import { postMessageToParent } from '@selfserve/react-common-data';

const StyledConfirmedTitle = styled(StyledTitle)(
  ({ theme }: { theme?: any }) => ({
    marginBottom: theme.spacing(2),
    lineHeight: '50px',
  })
);

const StyledConfirmedText = styled(StyledText)(
  ({ theme }: { theme?: any }) => ({
    marginBottom: theme.spacing(6),
    fontSize: theme.typography.pxToRem(24),
    lineHeight: '32px',
    fontWeight: '500',
    [theme.breakpoints.down('md')]: {
      marginBottom: '40px',
    },
  })
);

const StyledUseCaseContainer = styled('div')(({ theme }: { theme?: any }) => ({
  maxWidth: '1038px',
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(2),
  },
}));

const StyledUseCaseButton = styled(StyledButton)(
  ({ theme, selected }: { theme?: any; selected: boolean }) => ({
    maxWidth: '330px',
    width: '100%',
    height: '58px',
    margin: `0 ${theme.spacing(1)} ${theme.spacing(2)}`,
    borderColor: theme.palette.grey['400'],
    borderRadius: '8px',
    fontSize: theme.typography.pxToRem(18),
    [theme.breakpoints.down('md')]: {
      height: '48px',
    },
    ...(selected && {
      borderColor: theme.palette.primary.main,
      background: 'rgba(0, 110, 250, 0.05)',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        background: 'rgba(0, 110, 250, 0.05)',
      },
    }),
  })
);

const StyledConfirmedButton = styled(StyledButton)(
  ({ theme }: { theme?: any }) => ({
    width: '160px',
    [theme.breakpoints.down('md')]: {
      width: '98px',
    },
  })
);

const StyledAnimationContainer = styled('div')(
  ({
    theme,
    width,
    height,
  }: {
    theme?: any;
    width: number;
    height: number;
  }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    svg: {
      width: `${width - 20}px !important`,
      height: `${height - 10}px !important`,
    },
  })
);

interface Props {
  theme: any;
}

export const Confirmed = withTheme(({ theme }: Props) => {
  const { t } = useTranslation();
  const { subscriptionId } = useParams();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { width, height } = useScreenSizes();
  const { View: ConfirmedAnimation, play } = useSVGAnimation(
    ConfirmedAnimationJson,
    {
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
      loop: false,
      autoplay: false,
    }
  );
  const {
    redirectToLoginPage,
    status: getLoginUrlStatus,
    loginPage,
    requestType,
    isFetching
  } = useGetLoginUrl();
  const { data: { data: useCases } = {}, isLoading: isFetchLoading } =
    useUseCaseRequest(subscriptionId);
  const {
    mutateAsync: selectUseCases,
    isLoading: isSelectLoading,
    data: { status: selectStatus } = {},
  } = useUseCaseSelect(subscriptionId);
  const [selectedCases, setSelectedCases] = useState<IIndexSignature<string>>(
    {}
  );
  const [kmeLoginTrigger, setKmeLoginTrigger] = useState(false);

  const handleSetUseCase = useCallback(
    (useCase: string) => () =>
      setSelectedCases((prevCases) => {
        const newUseCases = {
          ...prevCases,
        };
        if (newUseCases[useCase]) {
          delete newUseCases[useCase];
        } else {
          newUseCases[useCase] = useCase;
        }
        return newUseCases;
      }),
    []
  );

  const handleButtonClick = useCallback(() => {
    if (!isSelectLoading) {
      const allSelectedCases = Object.values(selectedCases);
      if (allSelectedCases.length) {
        selectUseCases(allSelectedCases);
      } else {
        if (requestType !== 'POST') {
          redirectToLoginPage();
        } else {
          setKmeLoginTrigger(true);
        }
      }
    }
  }, [
    selectUseCases,
    selectedCases,
    redirectToLoginPage,
    isSelectLoading,
    requestType,
  ]);

  useEffect(() => {
    if (!isFetchLoading) {
      play();
    }
  }, [play, isFetchLoading]);

  useEffect(() => {
    if (!isSelectLoading && selectStatus) {
      if (requestType !== 'POST') {
        redirectToLoginPage();
      } else {
        setKmeLoginTrigger(true);
      }
    }
  }, [isSelectLoading, selectStatus, redirectToLoginPage, requestType]);

  const isInAppContext = useMemo(() => {
    return !!sessionStorage.getItem(storageKeys.IS_IN_APP_CONTEXT)
  }, []);

  const [productName] = useState<string | null>(() => {
    const planSettings = sessionStorage.getItem(storageKeys.PLAN_SETTINGS);
    if (!planSettings) return null;
    try {
      const parsedObj = JSON.parse(planSettings) || {};
      return parsedObj?.product || null;
    } catch (e) {
      return null;
    }
  });

  useEffect(() => {
    postMessageToParent({
      id: 'unisphere.services.iframes.emit',
      version: '1.0.0',
      payload: {
        id: 'unisphere.widget.plg.events.upgrade-flow-change-page',
        version: '1.0.0',
        payload: {
          isInitial: false,
          isFinalPage: true, // Let the parent know the user is on the final page
        },
      }
    });
    trackPageLoadEvent(
      PageType.View,
      'upgrade_complete',
      ApplicationFeatures.SUBSCRIPTION_FLOW,
      productName
    );
  }, []);

  return (
    <>
      {requestType === 'POST' && loginPage && loginPage(kmeLoginTrigger)}
      <Helmet>
        <title>{t('titles.create-account')}</title>
      </Helmet>
      <StyledAnimationContainer width={width} height={height}>
        {ConfirmedAnimation}
      </StyledAnimationContainer>
      {!isFetchLoading && (
        <CommonLayout>
          <StyledContentContainer centered>
            <StyledConfirmedTitle centered>
              {t(`common.${smallScreen ? 'congrats' : 'congratulations'}`)}
            </StyledConfirmedTitle>
            <StyledConfirmedText centered>
              {t(
                `common.${
                  useCases?.length ? 'pick-use-cases' : 'everything-is-in-place'
                }`
              )}
            </StyledConfirmedText>
            {!!useCases?.length && (
              <StyledUseCaseContainer>
                {useCases.map((useCase) => (
                  <StyledUseCaseButton
                    key={useCase}
                    selected={!!selectedCases[useCase]}
                    onClick={handleSetUseCase(useCase)}
                    isBorderless
                  >
                    {useCase}
                  </StyledUseCaseButton>
                ))}
              </StyledUseCaseContainer>
            )}
            {isInAppContext ? (
              <StyledConfirmedButton
                className="continue-button"
                id={buttonIds.confirmedLogin}
                fullWidth
                onClick={() => {
                  sessionStorage.removeItem(storageKeys.IS_IN_APP_CONTEXT);
                  postMessageToParent({
                    id: 'unisphere.services.iframes.emit',
                    version: '1.0.0',
                    payload: {
                      id: 'unisphere.widget.plg.events.upgrade-flow-completed',
                      version: '1.0.0',
                      payload: {}
                    }
                  });
                }}
              >
                Done
              </StyledConfirmedButton>
            ) : (
              <StyledLink
                disabled={isSelectLoading || kmeLoginTrigger || isFetching}
                onClick={() => {
                  if (requestType !== 'POST') {
                    useCases?.length
                      ? handleButtonClick()
                      : redirectToLoginPage();
                  } else {
                    setKmeLoginTrigger(true);
                  }
                }}
                to=""
              >
                <StyledConfirmedButton
                  className="continue-button"
                  type="submit"
                  id={buttonIds.confirmedLogin}
                  loading={isSelectLoading || kmeLoginTrigger || isFetching}
                  fullWidth
                >
                  {t(`common.${smallScreen ? 'continue' : 'login'}`)}
                </StyledConfirmedButton>
              </StyledLink>
            )}
          </StyledContentContainer>
        </CommonLayout>
      )}
    </>
  );
});
